// ─────────────────────────────────────────────────────────────────
// Glass-i — Item Master pure helpers (Stage 6B.8A-4).
//
// Plain-JS module so Node's built-in `node:test` runner can import it
// without a JSX loader and without resolving the axios `client.js`
// chain (Vite resolves extensionless imports; Node ESM does not).
// The JSX components and `itemsApi.js` re-export from here so the
// user-facing API stays one import per item.
// ─────────────────────────────────────────────────────────────────

// ── camelCase grid field → snake_case backend whitelist key.
//    Mirrors `services/erp/masters/items/items.constants.js` →
//    ITEM_GRID_ALLOWED_FIELDS. The reusable grid emits the column
//    `dataIndex` verbatim in filter[].field / sort[].field / groupBy;
//    this map normalises them to the snake_case keys the backend's
//    gridQueryParser whitelist understands.
export const GRID_FIELD_TO_SQL = Object.freeze({
  itemCode:          'item_code',
  itemName:          'item_name',
  skucode:           'skucode',
  barcode:           'barcode',
  hsnSac:            'hsn_sac',
  hsncode:           'hsn_sac',
  itemType:          'item_type',
  itemCategoryId:    'item_category_id',
  itemCategoryCode:  'item_category_code',
  itemCategoryName:  'item_category_name',
  materialTypeId:    'material_type_id',
  materialTypeCode:  'material_type_code',
  materialTypeName:  'material_type_name',
  materialFamily:    'material_family',
  defaultUomId:      'default_uom_id',
  defaultUomCode:    'default_uom_code',
  gstRatePercent:    'gst_rate_percent',
  taxabilityType:    'taxability_type',
  isGoods:           'is_goods',
  isService:         'is_service',
  isStockItem:       'is_stock_item',
  isPurchasable:     'is_purchasable',
  isSellable:        'is_sellable',
  isManufacturable:  'is_manufacturable',
  isJobworkItem:     'is_jobwork_item',
  status:            'status',
  salesRate:         'sales_rate',
  purchaseRate:      'purchase_rate',
  mrp:               'mrp',
  sourceStockItemId: 'source_stock_item_id',
  createdAt:         'created_at',
  updatedAt:         'updated_at'
});

export function mapField(camel) {
  if (!camel || typeof camel !== 'string') return camel;
  return GRID_FIELD_TO_SQL[camel] || camel;
}

function translateAdvancedRules(rules) {
  return rules.map((r) => {
    if (!r || typeof r !== 'object') return r;
    if (Array.isArray(r.rules)) return { ...r, rules: translateAdvancedRules(r.rules) };
    if (typeof r.field === 'string' && r.field) return { ...r, field: mapField(r.field) };
    return r;
  });
}

export function translateQuery(q) {
  if (!q || typeof q !== 'object') return q;
  const out = { ...q };
  if (Array.isArray(out.filters)) {
    out.filters = out.filters
      .filter((f) => f && f.field)
      .map((f) => ({ ...f, field: mapField(f.field) }));
  }
  if (Array.isArray(out.sort)) {
    out.sort = out.sort
      .filter((s) => s && s.field)
      .map((s) => ({ ...s, field: mapField(s.field) }));
  }
  if (typeof out.groupBy === 'string' && out.groupBy) {
    out.groupBy = mapField(out.groupBy);
  }
  if (out.advancedFilters && Array.isArray(out.advancedFilters.rules)) {
    out.advancedFilters = {
      ...out.advancedFilters,
      rules: translateAdvancedRules(out.advancedFilters.rules)
    };
  }
  return out;
}

// ── Filter-state shape + grid-filters translator ────────────────
//
// `ITEM_FILTER_FIELDS` is the canonical map from frontend filter
// state keys (camelCase) to backend SQL column names (snake_case).
// Mirrors `backend/.../items.constants.js → ITEM_GRID_ALLOWED_FIELDS`.
export const ITEM_FILTER_FIELDS = Object.freeze({
  status:           'status',
  itemCategoryId:   'item_category_id',
  materialTypeId:   'material_type_id',
  isStockItem:      'is_stock_item',
  isSellable:       'is_sellable',
  isPurchasable:    'is_purchasable',
  isManufacturable: 'is_manufacturable',
  isJobworkItem:    'is_jobwork_item',
  isGoods:          'is_goods',
  isService:        'is_service'
});

export const INITIAL_ITEM_FILTER_STATE = Object.freeze({
  status:           '',
  itemCategoryId:   '',
  materialTypeId:   '',
  isStockItem:      '',
  isSellable:       '',
  isPurchasable:    '',
  isManufacturable: '',
  isJobworkItem:    '',
  isGoods:          '',
  isService:        ''
});

// Turn local filter state → grid `filters[]` array.
//
//   ''      → omit
//   'true'  → boolean true
//   'false' → boolean false
//   else    → omit (for tri-states) or string passthrough (for ids / status)
export function toGridFilters(state) {
  if (!state || typeof state !== 'object') return [];
  const out = [];
  for (const [key, sqlField] of Object.entries(ITEM_FILTER_FIELDS)) {
    const raw = state[key];
    if (raw === '' || raw === null || raw === undefined) continue;
    if (key === 'status' || key === 'itemCategoryId' || key === 'materialTypeId') {
      out.push({ field: sqlField, value: raw });
      continue;
    }
    if (raw === 'true' || raw === 'false') {
      out.push({ field: sqlField, value: raw === 'true' });
    }
    // Anything else for a tri-state is dropped silently.
  }
  return out;
}

// ── Flag-tag descriptors + goods/service classifier ─────────────
export function describeItemFlags(record) {
  if (!record || typeof record !== 'object') return [];
  return [
    { key: 'stock',    letter: 'S', tooltip: 'Stock item',     color: 'default', on: record.isStockItem      === true },
    { key: 'sellable', letter: 'L', tooltip: 'Sellable',       color: 'blue',    on: record.isSellable       === true },
    { key: 'purch',    letter: 'P', tooltip: 'Purchasable',    color: 'cyan',    on: record.isPurchasable    === true },
    { key: 'mfg',      letter: 'M', tooltip: 'Manufacturable', color: 'purple',  on: record.isManufacturable === true },
    { key: 'jobwork',  letter: 'J', tooltip: 'Jobwork item',   color: 'gold',    on: record.isJobworkItem    === true },
    { key: 'consum',   letter: 'C', tooltip: 'Consumable',     color: 'default', on: record.isConsumable     === true }
  ];
}

export function classifyItemKind(record) {
  if (!record || typeof record !== 'object') return 'none';
  const g = record.isGoods   === true;
  const s = record.isService === true;
  if (g && s) return 'both';
  if (g) return 'goods';
  if (s) return 'service';
  return 'none';
}

// ── Page-scope summary (used by the summary card strip) ─────────
export function summarizeItems(rows) {
  const out = {
    total: 0, active: 0, stock: 0,
    sellable: 0, jobworkOrService: 0
  };
  if (!Array.isArray(rows)) return out;
  out.total = rows.length;
  for (const r of rows) {
    if (!r) continue;
    if (r.status === 'active' || r.isActive === true) out.active += 1;
    if (r.isStockItem === true) out.stock += 1;
    if (r.isSellable === true)  out.sellable += 1;
    if (r.isJobworkItem === true || r.isService === true) out.jobworkOrService += 1;
  }
  return out;
}
